import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Utensils } from "lucide-react";
import { MEAL_LABELS } from "@/lib/api";
import { DAY_NAMES_FULL, formatDate } from "@/lib/weeklyPlanUtils";

export default function AddMealDialog({
  open,
  onOpenChange,
  selectedSlot,
  customMeal,
  setCustomMeal,
  onAssignCustom,
  recipes,
  recipeFilter,
  setRecipeFilter,
  recipeFilterMode,
  setRecipeFilterMode,
  onAssignRecipe,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] flex flex-col p-0" data-testid="add-meal-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-100">
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Legg til {selectedSlot ? (MEAL_LABELS[selectedSlot.meal]?.toLowerCase() || "måltid") : "måltid"}
            {selectedSlot?.date && <span className="block text-sm font-normal text-stone-500 mt-0.5">{DAY_NAMES_FULL[selectedSlot.date.getDay()]} {formatDate(selectedSlot.date)}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          {/* Custom meal input — top, compact */}
          <div className="mb-4">
            <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 block">Eige måltid (fri tekst)</label>
            <div className="flex gap-2">
              <input value={customMeal} onChange={(e) => setCustomMeal(e.target.value)} placeholder="t.d. Pizza, Rester frå i går…" className="flex-1 min-w-0 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none" data-testid="custom-meal-input" onKeyDown={(e) => e.key === "Enter" && onAssignCustom()} />
              <button onClick={onAssignCustom} disabled={!customMeal.trim()} className="inline-flex items-center justify-center rounded-md bg-navy-800 px-3 py-2 text-xs font-semibold text-white hover:bg-navy-900 disabled:opacity-50 flex-shrink-0" data-testid="add-custom-meal-btn">Legg til</button>
            </div>
          </div>

          {/* Recipe picker */}
          <div className="border-t border-stone-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Vel oppskrift</label>
              {/* Filter toggle: same meal_type vs all */}
              <div className="inline-flex rounded-md border border-stone-200 overflow-hidden text-[10px] font-semibold" data-testid="recipe-filter-mode">
                <button
                  onClick={() => setRecipeFilterMode("same")}
                  className={`px-2 py-1 transition-colors ${recipeFilterMode === "same" ? "bg-navy-800 text-white" : "bg-white text-stone-500 hover:bg-stone-50"}`}
                  data-testid="recipe-filter-same"
                >
                  {selectedSlot ? (MEAL_LABELS[selectedSlot.meal] || "Måltid") : "Same type"}
                </button>
                <button
                  onClick={() => setRecipeFilterMode("all")}
                  className={`px-2 py-1 border-l border-stone-200 transition-colors ${recipeFilterMode === "all" ? "bg-navy-800 text-white" : "bg-white text-stone-500 hover:bg-stone-50"}`}
                  data-testid="recipe-filter-all"
                >
                  Alle
                </button>
              </div>
            </div>

            <div className="relative mb-3">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400 pointer-events-none" />
              <input
                value={recipeFilter}
                onChange={(e) => setRecipeFilter(e.target.value)}
                placeholder="Søk oppskrift (tittel, kategori, tag)…"
                className="w-full rounded-md border border-stone-200 bg-white pl-8 pr-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none"
                data-testid="recipe-search-in-meal-dialog"
              />
            </div>

            {recipes.length === 0 ? (
              <div className="text-center py-8 text-stone-400"><Utensils className="h-8 w-8 mx-auto mb-2 opacity-50" /><p className="text-sm">Ingen oppskrifter enno.</p></div>
            ) : (() => {
              const q = recipeFilter.trim().toLowerCase();
              const targetMeal = selectedSlot?.meal;
              const filtered = recipes.filter((r) => {
                if (recipeFilterMode === "same" && targetMeal && r.meal_type && r.meal_type !== targetMeal) return false;
                if (!q) return true;
                const hay = [r.title, r.category, r.description, ...(r.tags || [])].filter(Boolean).join(" ").toLowerCase();
                return hay.includes(q);
              });
              if (filtered.length === 0) {
                return (
                  <div className="text-center py-6 text-stone-400 text-sm">
                    Ingen treff. <button onClick={() => setRecipeFilterMode("all")} className="underline text-navy-800">Vis alle</button>
                  </div>
                );
              }
              // Group by category
              const groups = {};
              filtered.forEach((r) => {
                const cat = (r.category || "Anna").trim() || "Anna";
                if (!groups[cat]) groups[cat] = [];
                groups[cat].push(r);
              });
              const sortedCats = Object.keys(groups).sort();
              return (
                <div className="space-y-4" data-testid="recipe-picker-list">
                  {sortedCats.map((cat) => (
                    <div key={cat}>
                      <h4 className="text-[10px] uppercase tracking-[0.15em] text-stone-400 font-bold mb-1.5">{cat} <span className="text-stone-300">({groups[cat].length})</span></h4>
                      <div className="space-y-1">
                        {groups[cat].map((recipe) => (
                          <button
                            key={recipe.id}
                            onClick={() => onAssignRecipe(recipe)}
                            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-sailcloth-100/50 transition-colors border border-transparent hover:border-stone-200 text-left"
                            data-testid={`recipe-option-${recipe.id}`}
                          >
                            {recipe.image_url ? (
                              <img src={recipe.image_url} alt="" loading="lazy" className="w-11 h-11 rounded-md object-cover bg-stone-100 flex-shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            ) : (
                              <div className="w-11 h-11 rounded-md bg-stone-100 flex items-center justify-center flex-shrink-0">
                                <Utensils className="h-4 w-4 text-stone-300" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-stone-800 truncate">{recipe.title}</div>
                              {(recipe.tags && recipe.tags.length > 0) && (
                                <div className="text-[10px] text-stone-400 truncate">{recipe.tags.slice(0, 3).join(" · ")}</div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
